import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Card,
} from '@folio/components';
import { PageHeader } from '@folio/components';
import { useNewBusinessProductIds } from '@folio/hooks/useNewBusinessProductIds';
import { type InitializeCustomer, useCustomerControllerInitializeCustomer } from 'korint-sdk';
import type { KorintModulesProductsConfig } from 'korint-sdk/react';
import { useKorintProductConfig } from 'korint-sdk/react';
import { groupBy, sortBy } from 'lodash';
import { sanitize } from '@folio/utils';
import { SearchIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';
import { ThemeIcon } from '@folio/components/ui/theme-icon';

type ProductEntry = {
  productId: string;
  config: KorintModulesProductsConfig;
};

export function ProductSelectionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { productsConfig } = useKorintProductConfig();
  const { productIds, isLoading } = useNewBusinessProductIds();

  const { mutate: initCustomer, isPending } = useCustomerControllerInitializeCustomer({
    mutation: {
      onSuccess: (customer: InitializeCustomer, variables: { params: { productId: string } }) => {
        const productId = variables.params.productId;
        const productConfig = productsConfig[productId];
        const productCustomPath = productConfig?.productCustomPath ?? '';

        navigate(
          `/discovery${productCustomPath}/${customer.id}/${customer.initializedWith.policy}/${customer.initializedWith.branch}`,
        );
      },
    },
  });

  const normalizedSearch = sanitize(search);

  const filteredProducts = useMemo(() => {
    if (!productIds?.length) return [];

    return productIds.filter(productId => {
      const config = productsConfig[productId];
      const corpus = sanitize(
        [
          t(`products.${productId}.label`),
          config.display.category,
          t(`products.categories.${config.display.category}`),
        ].join(''),
      );

      return !normalizedSearch || corpus.includes(normalizedSearch);
    });
  }, [productIds, productsConfig, normalizedSearch]);

  const groupedProducts = useMemo(() => {
    const entries: ProductEntry[] = filteredProducts.map(productId => ({
      productId,
      config: productsConfig[productId],
    }));

    const grouped = groupBy(entries, entry => entry.config.display.category);

    return sortBy(Object.entries(grouped), ([category]) =>
      t(`products.categories.${category}`, category),
    );
  }, [filteredProducts, productsConfig]);

  const categoryKeys = groupedProducts.map(([cat]) => cat);

  if (isLoading) {
    return null;
  }

  return (
    <div className="container mx-auto p-4">
      <PageHeader title={t('products.title')} subtitle={t('products.subtitle')} />

      <div className="relative mt-4 mb-6">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <input
          autoFocus
          type="text"
          placeholder={t('products.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="h-10 w-full rounded-md border border-input bg-background pl-10 pr-4 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </div>

      {groupedProducts.length === 0 && (
        <Stack align="center" justify="center" gap={0} className="py-16 text-muted-foreground">
          <SearchIcon className="size-12 mb-4 opacity-50" />
          <Text size="lg" weight="medium">
            {t('products.emptyTitle')}
          </Text>
          <Text size="sm">{t('products.emptyDescription')}</Text>
        </Stack>
      )}

      <Accordion
        key={categoryKeys.join(',')}
        type="multiple"
        defaultValue={categoryKeys}
        className="flex flex-col gap-6"
      >
        {groupedProducts.map(([category, products]) => {
          const label = t(`products.categories.${category}`, category);

          return (
            <AccordionItem key={category} value={category} display="transparent">
              <AccordionTrigger display="transparent">
                {label} ({products.length})
              </AccordionTrigger>
              <AccordionContent display="transparent">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {sortBy(products, p => t(`products.${p.productId}.label`)).map(
                    ({ productId, config }) => (
                      <ProductCard
                        key={productId}
                        productId={productId}
                        icon={config.display.icon as React.ComponentType<{ className?: string }>}
                        category={config.display.category}
                        badges={config.display.badges}
                        isPending={isPending}
                        onClick={() => initCustomer({ data: {}, params: { productId } })}
                      />
                    ),
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

function ProductCard({
  productId,
  icon: Icon,
  category,
  badges,
  isPending,
  onClick,
}: {
  productId: string;
  icon: React.ComponentType<{ className?: string }>;
  category: string;
  badges: string[];
  isPending: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();

  const label = t(`products.${productId}.label`);
  const description = t(`products.${productId}.description`);

  return (
    <Card
      className="group cursor-pointer transition-shadow hover:shadow-md hover:border-primary/30 min-h-36"
      onClick={isPending ? undefined : onClick}
    >
      <Stack gap={3} className="p-4 h-full">
        <Group align="start" justify="between">
          <Group gap={3}>
            <ThemeIcon>
              <Icon />
            </ThemeIcon>
            <Title order={3} size="sm" className="leading-tight font-medium">
              {label}
            </Title>
          </Group>
        </Group>

        {description && (
          <Text size="xs" tone="muted" className="line-clamp-2">
            {description}
          </Text>
        )}

        {(category || badges.length > 0) && (
          <Group align="stretch" wrap gap={0} className="gap-1.5">
            <Badge variant="outline" className="text-[10px]">
              {t(`products.categories.${category}`)}
            </Badge>
            {badges.map(badge => (
              <Badge key={badge} variant="default" className="text-[10px]">
                {t(`products.badges.${badge}`)}
              </Badge>
            ))}
          </Group>
        )}
      </Stack>
    </Card>
  );
}
